// Email Service using EmailJS
// This service handles sending emails from the contact form

import emailjs from 'emailjs-com';

// EmailJS Configuration
const EMAILJS_CONFIG = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_orvin',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_orvin',
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key',
};

/**
 * Initialize EmailJS
 * Call this once when the app starts
 */
export const initializeEmailService = () => {
  try {
    emailjs.init(EMAILJS_CONFIG.publicKey);
    console.log('✅ Email service initialized');
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize email service:', error);
    return false;
  }
};

/**
 * Send contact form email
 * @param {Object} formData - The form data to send
 * @param {string} formData.name - Sender's name
 * @param {string} formData.email - Sender's email
 * @param {string} formData.subject - Email subject
 * @param {string} formData.message - Email message
 * @returns {Promise<Object>} Result object with success status
 */
export const sendContactEmail = async (formData) => {
  try {
    // Validate form data
    if (!formData.name || !formData.email || !formData.message) {
      throw new Error('Missing required fields');
    }

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Invalid email format');
    }

    // Prepare template parameters for the main email (to Orvin)
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      to_email: 'orvin.contact@gmail.com',
      subject: formData.subject || 'New Contact Form Submission',
      message: formData.message,
      reply_to: formData.email,
    };

    // Send email to Orvin via EmailJS
    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );

    console.log('✅ Email sent successfully to Orvin:', response.status);

    // Send auto-reply confirmation email to the user
    await sendAutoReplyEmail(formData);

    return {
      success: true,
      message: '✅ Message received! We\'ll contact you soon.',
      status: response.status,
    };
  } catch (error) {
    console.error('❌ Failed to send email:', error);

    return {
      success: false,
      message: error.message || 'Failed to send email. Please try again.',
      error: error,
    };
  }
};

/**
 * Send auto-reply confirmation email to user
 * @param {Object} formData - The form data from the contact form
 * @returns {Promise<Object>} Result object with success status
 */
export const sendAutoReplyEmail = async (formData) => {
  try {
    // Auto-reply template ID (you'll create this in EmailJS)
    const autoReplyTemplateId = process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID || EMAILJS_CONFIG.templateId;

    // Prepare auto-reply parameters
    const autoReplyParams = {
      to_name: formData.name,
      to_email: formData.email,
      from_name: 'Orvin Creative Agency',
      from_email: 'orvin.contact@gmail.com',
      subject: 'We received your message - Orvin Creative Agency',
      user_message: formData.message,
      user_subject: formData.subject || 'General Inquiry',
    };

    // Send auto-reply email
    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      autoReplyTemplateId,
      autoReplyParams,
      EMAILJS_CONFIG.publicKey
    );

    console.log('✅ Auto-reply sent successfully to user:', response.status);

    return {
      success: true,
      message: 'Auto-reply sent',
      status: response.status,
    };
  } catch (error) {
    // Log error but don't fail the main operation
    console.error('⚠️ Auto-reply failed (non-critical):', error);
    
    return {
      success: false,
      message: 'Auto-reply failed but main email was sent',
      error: error,
    };
  }
};

/**
 * Send email with rate limiting
 * Prevents spam by limiting how often emails can be sent
 */
const emailTimestamps = new Map();

export const sendContactEmailWithRateLimit = async (formData, userId = 'default') => {
  const now = Date.now();
  const lastSent = emailTimestamps.get(userId);
  const cooldownPeriod = 60000; // 1 minute

  // Check if user is trying to send too quickly
  if (lastSent && now - lastSent < cooldownPeriod) {
    const remainingTime = Math.ceil((cooldownPeriod - (now - lastSent)) / 1000);
    return {
      success: false,
      message: `Please wait ${remainingTime} seconds before sending another message.`,
    };
  }

  // Send the email
  const result = await sendContactEmail(formData);

  // Update timestamp if successful
  if (result.success) {
    emailTimestamps.set(userId, now);
  }

  return result;
};

/**
 * Validate contact form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result
 */
export const validateContactForm = (formData) => {
  const errors = {};

  // Name validation
  if (!formData.name || formData.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!formData.email || !emailRegex.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Message validation
  if (!formData.message || formData.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters';
  }

  // Subject validation (optional)
  if (formData.subject && formData.subject.length > 100) {
    errors.subject = 'Subject must be less than 100 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Sanitize user input to prevent XSS attacks
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/[<>]/g, '') // Remove < and > characters to prevent XSS
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
    .substring(0, 5000); // Limit length
};

export default {
  initializeEmailService,
  sendContactEmail,
  sendContactEmailWithRateLimit,
  validateContactForm,
  sanitizeInput,
};
